import { useEffect, useRef } from "react"
import type { GlobalEvent, PendingVisit } from "@inertiajs/core"
import { focusWithoutScroll } from "@/lib/focus-without-scroll"

type EditorGuard = {
  dirty: boolean
  submitting: boolean
  leaveMessage: string
  onDiscard: () => void
  onHistoryRequest: (proceed: () => void) => void
  onPendingNavigation: () => void
  onHistoryFailure: () => void
}

const submissions = new WeakSet<PendingVisit>()
const editors = new Set<EditorGuard>()
const accepted = new WeakSet<EditorGuard>()
let removeListeners: (() => void) | undefined
let applicationListeners = 0
let unloadListening = false

// Inertia calls the visit's onBefore before its global before event. Mark only
// that exact submission; unrelated visits must not inherit a saving bypass.
export function allowEditorSubmission(visit: PendingVisit) {
  submissions.add(visit)
}

function confirmLeaving() {
  const active = [...editors].filter(editor => !accepted.has(editor))
  const pending = active.find(editor => editor.submitting)
  if (pending) {
    pending.onPendingNavigation()
    return false
  }
  const dirty = active.find(editor => editor.dirty)
  if (!dirty) return true
  // Keep the original Inertia visit and its callbacks intact. Cancel-and-replay
  // from the global event would lose per-visit callbacks (including tenant flow).
  if (!window.confirm(dirty.leaveMessage)) return false
  active.forEach(editor => { accepted.add(editor) })
  syncUnloadListener()
  active.forEach(editor => editor.onDiscard())
  return true
}

function beforeVisit(event: GlobalEvent<"before">) {
  if (event.defaultPrevented) return
  const visit = event.detail.visit
  if (submissions.delete(visit) || visit.prefetch) return
  // Inertia 3.5 tags its automatic deferred-prop reloads internally. Allow only
  // those bounded, same-URL background reads, not arbitrary async navigation.
  const deferred = visit as PendingVisit & { deferredProps?: boolean }
  if (deferred.deferredProps && visit.method === "get" && visit.async &&
    visit.preserveState === true && visit.only.length > 0 &&
    visit.url.href === window.location.href) return
  if (!confirmLeaving()) event.preventDefault()
}

type HistoryRequest = {
  originKey: string
  targetKey: string
  targetURL: string
  focus: HTMLElement | null
  scrollX: number
  scrollY: number
  scrollRestoration: ScrollRestoration
  returning: boolean
}
let historyRequest: HistoryRequest | undefined

function activeEditors() { return [...editors].filter(editor => !accepted.has(editor)) }
function releaseListenersIfIdle() {
  if (editors.size || historyRequest) return
  removeListeners?.()
  removeListeners = undefined
}
function clearHistoryRequest() {
  const request = historyRequest
  historyRequest = undefined
  if (request) window.history.scrollRestoration = request.scrollRestoration
  releaseListenersIfIdle()
  return request
}
function traversalResult(result: NavigationResult, failed: (error: unknown) => void) {
  void result.committed?.catch(() => {})
  void result.finished?.catch(failed)
}

function beforeHistory(event: NavigateEvent) {
  const navigation = window.navigation
  if (!navigation) return
  if (historyRequest?.returning && event.destination.key === historyRequest.originKey) return
  // Observe only a cancelable same-document traversal. The popstate capture
  // restores its original entry before Inertia can replace the editor. Native
  // preventDefault corrupts later history traversal in the reviewed WebKit.
  if (event.defaultPrevented || event.navigationType !== "traverse" ||
    !event.destination.sameDocument || !event.cancelable) {
    if (event.navigationType === "traverse") clearHistoryRequest()
    return
  }
  if (!activeEditors().some(editor => editor.dirty || editor.submitting)) return
  const originKey = historyRequest?.originKey ?? navigation.currentEntry?.key
  if (!originKey) return
  historyRequest = {
    originKey,
    focus: historyRequest?.focus ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null),
    scrollX: historyRequest?.scrollX ?? window.scrollX,
    scrollY: historyRequest?.scrollY ?? window.scrollY,
    scrollRestoration: historyRequest?.scrollRestoration ?? window.history.scrollRestoration,
    targetKey: event.destination.key,
    targetURL: event.destination.url,
    returning: false,
  }
  window.history.scrollRestoration = "manual"
}

function historyFailure() {
  clearHistoryRequest()
  activeEditors().find(editor => editor.dirty || editor.submitting)?.onHistoryFailure()
}

function historyAborted() {
  // Another listener can cancel before popstate. There is then no entry to
  // restore. A failed return is handled by that traversal's promise instead.
  if (historyRequest && !historyRequest.returning) clearHistoryRequest()
}

function historyChanged(event: PopStateEvent) {
  const navigation = window.navigation
  const request = historyRequest
  if (!navigation || !request) return
  // Initialization registers before Inertia. Keep both the attempted and
  // restoration traversal out of its popstate listener; no page/data request occurs without consent.
  event.stopImmediatePropagation()
  if (navigation.currentEntry?.key !== request.originKey) {
    request.returning = true
    traversalResult(navigation.traverseTo(request.originKey), () => {
      if (historyRequest === request) historyFailure()
    })
    return
  }
  clearHistoryRequest()
  window.scrollTo(request.scrollX, request.scrollY)
  if (request.focus?.isConnected && !request.focus.closest("[inert]")) focusWithoutScroll(request.focus)
  const active = activeEditors()
  const pending = active.find(editor => editor.submitting)
  if (pending) { pending.onPendingNavigation(); return }
  const dirty = active.find(editor => editor.dirty)
  if (!dirty) return
  dirty.onHistoryRequest(() => {
    const current = activeEditors()
    const saving = current.find(editor => editor.submitting)
    if (saving) { saving.onPendingNavigation(); return }
    current.forEach(editor => { accepted.add(editor) })
    syncUnloadListener()
    current.forEach(editor => editor.onDiscard())
    traversalResult(navigation.traverseTo(request.targetKey), error => {
      if (error instanceof DOMException && error.name === "AbortError") return
      // Consent permits a normal load when the original destination was removed.
      window.location.assign(request.targetURL)
    })
  })
}

function syncUnloadListener() {
  const needed = activeEditors().some(editor => editor.dirty || editor.submitting)
  if (needed === unloadListening) return
  if (needed) window.addEventListener("beforeunload", beforeUnload)
  else window.removeEventListener("beforeunload", beforeUnload)
  unloadListening = needed
}

function beforeUnload(event: BeforeUnloadEvent) {
  if (![...editors].some(editor => !accepted.has(editor) && (editor.dirty || editor.submitting))) return
  event.preventDefault()
  event.returnValue = ""
}

function pageHidden() {
  clearHistoryRequest()
  // An accepted cross-document departure may place this document in bfcache.
  // Discard only once departure commits, never while beforeunload can cancel it.
  const active = [...editors].filter(editor => !accepted.has(editor))
  active.forEach(editor => { accepted.add(editor) })
  syncUnloadListener()
  active.forEach(editor => editor.onDiscard())
}

function ensureListeners() {
  if (!removeListeners) {
    const navigation = window.navigation
    document.addEventListener("inertia:before", beforeVisit)
    navigation?.addEventListener("navigate", beforeHistory)
    navigation?.addEventListener("navigateerror", historyAborted)
    const standalonePopstate = applicationListeners === 0
    if (standalonePopstate) window.addEventListener("popstate", historyChanged, true)
    window.addEventListener("pagehide", pageHidden)
    removeListeners = () => {
      document.removeEventListener("inertia:before", beforeVisit)
      navigation?.removeEventListener("navigate", beforeHistory)
      navigation?.removeEventListener("navigateerror", historyAborted)
      if (standalonePopstate) window.removeEventListener("popstate", historyChanged, true)
      window.removeEventListener("pagehide", pageHidden)
    }
  }
}

// Install before createInertiaApp. Native Chromium popstate dispatch invokes an
// earlier router listener before a later capture listener, unlike synthetic DOM
// dispatch and the other reviewed engines. Keep this ordering for the app lifetime.
export function initializeEditorNavigation() {
  if (applicationListeners++ === 0) window.addEventListener("popstate", historyChanged, true)
  let disposed = false
  return () => {
    if (disposed) return
    disposed = true
    if (--applicationListeners === 0) window.removeEventListener("popstate", historyChanged, true)
  }
}

export function registerEditorNavigationGuard(options: EditorGuard) {
  accepted.delete(options)
  editors.add(options)
  ensureListeners()
  syncUnloadListener()
  return () => {
    editors.delete(options)
    syncUnloadListener()
    releaseListenersIfIdle()
  }
}

export function useEditorNavigationGuard({ enabled, ...options }: EditorGuard & { enabled: boolean }) {
  const latest = useRef(options)
  latest.current = options
  useEffect(() => {
    if (!enabled) return
    // Keep the registry and history restoration stable through form/dialog renders.
    return registerEditorNavigationGuard({
      get dirty() { return latest.current.dirty },
      get submitting() { return latest.current.submitting },
      get leaveMessage() { return latest.current.leaveMessage },
      onDiscard: () => latest.current.onDiscard(),
      onHistoryRequest: proceed => latest.current.onHistoryRequest(proceed),
      onPendingNavigation: () => latest.current.onPendingNavigation(),
      onHistoryFailure: () => latest.current.onHistoryFailure(),
    })
  }, [enabled])
  useEffect(syncUnloadListener, [enabled, options.dirty, options.submitting])
}
